import React, {
  memo,
  useCallback,
  useContext,
  useMemo,
  useEffect,
  useState,
  useRef,
} from 'react';

import { createPortal } from 'react-dom';

import { analytics } from '../../../..';

import {
  AppContext,
  ColorContext,
  DataContext,
  MapContext,
} from '../../../../contexts';

import {
  Dialog,
  Abas,
  Aba,
} from '../../..';

import useStorage from '../../../../hooks/useStorage';

import WindowStorage from '../../../../utils/storage';

import AbaColorirResultadoFiltro from './components/AbaColorirResultadoFiltro';
import AbaColorirRegioes from './components/AbaColorirRegioes';

import { LegendaAuxiliarCores } from './components/RegrasDeCores';

import { AbaWrapper } from './styles';
import { gradientOpacities, gradientOpacityRange } from '../../../../constants/mapColoring';
import { getBrightness } from '../../../../utils';

const STORE_INICIAL = { data: new Date() };
const storageDialogoCores = WindowStorage.localStorage('dialogo-cores');

function DialogoCores() {
  const {
    activeFilters,
    setAuxiliarBox,
    setOptions,
  } = useContext(AppContext);

  const {
    metodoColoracao,
    calcularGradienteDoValor,
    isDialogoCoresVisivel,
    setMetodoColoracao,
    setDialogoCoresVisivel,
  } = useContext(ColorContext);

  const {
    getResult,
  } = useContext(DataContext);

  const {
    visibleCities,
    setShowColors,
  } = useContext(MapContext);

  const dispatchStoreDialogoCores = useStorage(storageDialogoCores, STORE_INICIAL);

  const dadosFiltro = useMemo(() => getResult(), [getResult]);

  const [aba, setAba] = useState('aba-regioes');

  useEffect(() => {
    setAba(() => {
      if (metodoColoracao.tipo === 'por-regiao') return 'aba-regioes';
      return 'aba-filtros';
    });
  }, [metodoColoracao.tipo]);

  const refDialogoCores = useRef(null);

  const criarPortalActions = useCallback((node) => (
    refDialogoCores.current
      ? createPortal(
        node,
        refDialogoCores.current.querySelector('.MuiDialogActions-root')
      )
      : null
  ), []);

  const fechar = useCallback(() => setDialogoCoresVisivel(false), [setDialogoCoresVisivel]);

  const calcularIntervaloGradiente = useCallback(() => {
    const resultadosVisiveis = Object.entries(dadosFiltro.result)
      .reduce((total, [key, value]) => {
        if (visibleCities.length === 0 || visibleCities.includes(key)) {
          return { ...total, [key]: value };
        }
        return total;
      }, {});

    // gera Array de valores distintos e organiza
    const valoresDistintos = Array.from(new Set(Object.values(resultadosVisiveis)))
      .sort((a, b) => a - b);

    const intervaloValores = [];
    gradientOpacities.forEach((opacity) => {
      const value = valoresDistintos.at(-1) * opacity;

      let closestValue;
      function setClosestValue(v) { closestValue = v; }

      valoresDistintos.some((distinct, index, list) => {
        if (distinct === value) {
          setClosestValue(distinct);
          return true;
        }

        const distinctOpacity = distinct / valoresDistintos.at(-1);
        const [startRange, endRange] = gradientOpacityRange(opacity);
        const inRange = startRange <= distinctOpacity && distinctOpacity < endRange;
        if (!inRange) return false;

        if (!closestValue) {
          setClosestValue(distinct);
          return false;
        }

        const closestDiff = Math.abs(value - closestValue);
        const currentDiff = Math.abs(value - distinct);
        if (currentDiff < closestDiff) {
          setClosestValue(distinct);
          // se a diferença anterior (value - list[index - 1]) for menor,
          // significa que estamos nos afastando do valor,
          // logo interrompemos as iterações
          return Math.abs(value - list[index - 1]) < currentDiff;
          // retorna true e interrompe as iterações
          // senão false e continua
        }

        return false;
      });

      if (closestValue) intervaloValores.push(closestValue);
    });

    const intervalo = intervaloValores
      .map(valor => ({ valor, cor: calcularGradienteDoValor(valor, valoresDistintos.at(-1)) }));

    return intervalo;
  }, [calcularGradienteDoValor, dadosFiltro.result, visibleCities]);

  const calcularIntervaloGradienteFiltro = useCallback((filterColorRule) => {
    const filter = activeFilters.find((item) => item.settings.title.join(', ') === filterColorRule.item);
    if (!filter) return [];

    const resultadosVisiveis = Object.entries(filter.result)
      .reduce((total, [key, value]) => {
        if (visibleCities.length === 0 || visibleCities.includes(key)) {
          return { ...total, [key]: value };
        }
        return total;
      }, {});

    // gera Array de valores distintos e organiza
    const valoresDistintos = Array.from(new Set(Object.values(resultadosVisiveis)))
      .sort((a, b) => a - b);

    const intervaloValores = [];
    gradientOpacities.forEach((opacity) => {
      const value = valoresDistintos.at(-1) * opacity;

      let closestValue;
      function setClosestValue(v) { closestValue = v; }

      valoresDistintos.some((distinct, index, list) => {
        if (distinct === value) {
          setClosestValue(distinct);
          return true;
        }

        const distinctOpacity = distinct / valoresDistintos.at(-1);
        const [startRange, endRange] = gradientOpacityRange(opacity);
        const inRange = startRange <= distinctOpacity && distinctOpacity < endRange;
        if (!inRange) return false;

        if (!closestValue) {
          setClosestValue(distinct);
          return false;
        }

        const closestDiff = Math.abs(value - closestValue);
        const currentDiff = Math.abs(value - distinct);
        if (currentDiff < closestDiff) {
          setClosestValue(distinct);
          // se a diferença anterior (value - list[index - 1]) for menor,
          // significa que estamos nos afastando do valor,
          // logo interrompemos as iterações
          return Math.abs(value - list[index - 1]) < currentDiff;
          // retorna true e interrompe as iterações
          // senão false e continua
        }

        return false;
      });

      if (closestValue) intervaloValores.push(closestValue);
    });

    const intervalo = intervaloValores
      .map(valor => ({
        valor,
        cor: calcularGradienteDoValor(valor, valoresDistintos.at(-1), filterColorRule.cor)
      }));

    return intervalo;
  }, [calcularGradienteDoValor, activeFilters, visibleCities]);

  const getLegendaAuxiliar = useCallback(() => {
    switch (metodoColoracao.tipo) {
      case 'gradiente':
        return (
          <LegendaAuxiliarCores
            regrasCor={calcularIntervaloGradiente()}
            renderizar={(regra) => Number(regra.valor).toLocaleString('pt-BR')}
          />
        );
      case 'escala-cores':
        return (
          <LegendaAuxiliarCores
            regrasCor={metodoColoracao.regras}
            renderizar={(regra) => (
              <>
                {Number(regra.de).toLocaleString('pt-BR')}
                {' - '}
                {
                  regra.ate !== Number.MAX_VALUE && !Number.isNaN(regra.ate)
                    ? regra.ate.toLocaleString('pt-BR')
                    : '...'
                }
              </>
            )}
          />
        );
      case 'gradiente-por-filtro':
        return (
          <LegendaAuxiliarCores
            regrasCor={metodoColoracao.regras.map((regra) => ({
              id: regra.id,
              item: regra.item,
              intervalo: calcularIntervaloGradienteFiltro(regra)
            }))}
            renderizar={(regra) => (
              <>
                <div>{regra.item}</div>
                {
                  regra.intervalo.map((colorStep) => {
                    const light = getBrightness(colorStep.cor);
                    const [, opacityCapture] = colorStep.cor.match(/,([0-9.]+)\)$/);
                    return (
                      <span
                        style={{
                          display: 'inline-block',
                          backgroundColor: colorStep.cor,
                          marginInline: 2,
                          padding: '1px 4px',
                        }}
                      >
                        <span
                          style={{
                            color: Number(opacityCapture) < 0.4 ? 'white' : (light < 80 ? 'white' : 'black'),
                            mixBlendMode: Number(opacityCapture) < 0.4 ? 'difference' : (light < 80 ? 'lighten' : 'darken'),
                          }}
                        >
                          {Number(colorStep.valor).toLocaleString('pt-BR')}
                        </span>
                      </span>
                    );
                  })
                }
              </>
            )}
          />
        );
      case 'escala-por-filtro':
        return (
          <LegendaAuxiliarCores
            regrasCor={metodoColoracao.regras}
            renderizar={(regra) => (
              <>
                {regra.item}
                {
                  regra.intervalo && (
                    <>
                      <br />
                      {'De '}
                      {Number(regra.intervalo.de).toLocaleString('pt-BR')}
                      {' a '}
                      {
                        (
                          !Number.isNaN(regra.intervalo.ate)
                          && regra.intervalo.ate !== Number.MAX_VALUE
                            ? regra.intervalo.ate.toLocaleString('pt-BR')
                            : '...'
                        )
                      }
                    </>
                  )
                }
              </>
            )}
          />
        );
      default: return <></>;
    }
  }, [
    calcularIntervaloGradiente,
    calcularIntervaloGradienteFiltro,
    metodoColoracao.tipo,
    metodoColoracao.regras
  ]);

  const aplicarLegenda = useCallback(() => {
    if (dadosFiltro.settings) {
      return setAuxiliarBox({
        state: 'visible',
        title: 'Legenda',
        description: dadosFiltro.settings.title || [],
        content: getLegendaAuxiliar(),
      });
    }

    if (Object.values(dadosFiltro).reduce((n, lista) => n + lista.length, 0) > 1) {
      return setAuxiliarBox({
        state: 'visible',
        title: 'Legenda',
        description: ['Regras do Filtro'],
        content: getLegendaAuxiliar(),
      });
    }

    return setAuxiliarBox({
      state: 'hidden',
      title: 'Legenda',
      description: [],
      content: <></>,
    });
  }, [dadosFiltro, getLegendaAuxiliar, setAuxiliarBox]);

  const onAplicar = useCallback((novoModo, fecharDialogo = true) => {
    setMetodoColoracao(() => {
      setDialogoCoresVisivel(!fecharDialogo);
      setOptions(options => ({ ...options, showColors: true }));
      setShowColors(true);
      dispatchStoreDialogoCores({ tipo: 'ALTERA_E_PERSISTE', metodoColoracao: novoModo });
      analytics.logEvent('dialog-cores-aplicar-coloracao');
      return novoModo;
    });
  }, [
    dispatchStoreDialogoCores,
    setDialogoCoresVisivel,
    setOptions,
    setMetodoColoracao,
    setShowColors,
  ]);

  useEffect(() => aplicarLegenda(), [aplicarLegenda]);

  return (
    <Dialog
      ref={refDialogoCores}
      disableBackdropClick
      title="Cores"
      show={isDialogoCoresVisivel}
      onClose={fechar}
    >
      <Abas
        value={aba}
        onChange={(_, aba) => setAba(aba)}
        contexto={{
          criarPortalActions,
          dispatchStoreDialogoCores,
          onAplicar,
          refDialogoCores,
        }}
        renderizarAbasEm={<AbaWrapper />}
      >
        <Aba
          value="aba-regioes"
          label="Regiões"
          disabled={activeFilters.length > 0}
          componente={<AbaColorirRegioes />}
        />
        <Aba
          value="aba-filtros"
          label="Filtros"
          disabled={activeFilters.length === 0}
          componente={<AbaColorirResultadoFiltro />}
        />
      </Abas>
    </Dialog>
  );
}

export default memo(DialogoCores);
